import React, { useState, useCallback, useRef } from 'react';
import {
  Satellite,
  Play,
  Square,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  RefreshCw,
} from 'lucide-react';
import {
  fetchAllLiveTracking,
  retryFailedTrackingCodes,
  fetchUnresolvedFailures,
  type LiveTrackingProgress,
  type FailedTrackingCode,
} from '../../lib/trackingService';

interface LiveTrackingBannerProps {
  adminId: string;
  totalTracked: number;
  estimatedCount: number;
  onComplete: () => void;
}

type SyncState = 'idle' | 'running' | 'completed' | 'cancelled' | 'retrying';

function formatEta(elapsedMs: number, processed: number, total: number): string {
  if (processed <= 0 || total <= 0) return 'Calculating...';
  const msPerItem = elapsedMs / processed;
  const remaining = (total - processed) * msPerItem;
  if (remaining < 60000) return `${Math.ceil(remaining / 1000)}s remaining`;
  if (remaining < 3600000) return `${Math.ceil(remaining / 60000)}m remaining`;
  const hrs = Math.floor(remaining / 3600000);
  const mins = Math.ceil((remaining % 3600000) / 60000);
  return `${hrs}h ${mins}m remaining`;
}

function formatDuration(ms: number): string {
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3600000) {
    const m = Math.floor(ms / 60000);
    const s = Math.round((ms % 60000) / 1000);
    return `${m}m ${s}s`;
  }
  const h = Math.floor(ms / 3600000);
  const m = Math.ceil((ms % 3600000) / 60000);
  return `${h}h ${m}m`;
}

export const LiveTrackingBanner: React.FC<LiveTrackingBannerProps> = ({
  adminId,
  totalTracked,
  estimatedCount,
  onComplete,
}) => {
  const [state, setState] = useState<SyncState>('idle');
  const [progress, setProgress] = useState<LiveTrackingProgress | null>(null);
  const [result, setResult] = useState<{ succeeded: number; failed: number; total: number; duration: number; failedCodes: FailedTrackingCode[] } | null>(null);
  const [expanded, setExpanded] = useState(true);
  const abortRef = useRef<AbortController | null>(null);

  const pct = progress && progress.total > 0
    ? Math.min(100, Math.round((progress.processed / progress.total) * 100))
    : 0;

  const handleStart = useCallback(async (forceAll = false) => {
    if (state === 'running' || state === 'retrying') return;
    setState('running');
    setResult(null);
    setExpanded(true);

    const controller = new AbortController();
    abortRef.current = controller;
    const startTime = Date.now();

    try {
      const res = await fetchAllLiveTracking(
        adminId,
        (p) => setProgress(p),
        controller.signal,
        forceAll
      );

      const duration = Date.now() - startTime;
      setResult({ ...res, duration });
      setState(controller.signal.aborted ? 'cancelled' : 'completed');
      onComplete();
    } catch {
      setState('completed');
      onComplete();
    } finally {
      abortRef.current = null;
    }
  }, [adminId, state, onComplete]);

  const handleRetryFailed = useCallback(async () => {
    if (state === 'running' || state === 'retrying') return;

    let codesToRetry: string[] = [];

    if (result?.failedCodes?.length) {
      codesToRetry = result.failedCodes.map((f) => f.trackingCode);
    } else {
      const failures = await fetchUnresolvedFailures(adminId);
      codesToRetry = failures.map((f) => f.tracking_code);
    }

    if (codesToRetry.length === 0) return;

    setState('retrying');
    setProgress(null);
    setExpanded(true);

    const controller = new AbortController();
    abortRef.current = controller;
    const startTime = Date.now();

    try {
      const res = await retryFailedTrackingCodes(
        adminId,
        codesToRetry,
        (p) => setProgress(p),
        controller.signal
      );

      const duration = Date.now() - startTime;
      setResult({ ...res, duration });
      setState(controller.signal.aborted ? 'cancelled' : 'completed');
      onComplete();
    } catch {
      setState('completed');
      onComplete();
    } finally {
      abortRef.current = null;
    }
  }, [adminId, state, result, onComplete]);

  const handleCancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const handleDismiss = useCallback(() => {
    setState('idle');
    setProgress(null);
    setResult(null);
  }, []);

  if (state === 'idle' && totalTracked === 0) return null;

  const isActive = state === 'running' || state === 'retrying';

  return (
    <div className="mb-6">
      {state === 'idle' && (
        <div className="relative overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="absolute inset-0 bg-gradient-to-r from-teal-500/5 via-cyan-500/5 to-blue-500/5" />
          <div className="relative px-5 py-4 sm:px-6 sm:py-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-teal-500/20">
                <Satellite className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Fetch Live Tracking Data from PostNL
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Sync real-time delivery statuses for{' '}
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {totalTracked.toLocaleString()}
                  </span>{' '}
                  tracked packages
                  {estimatedCount > 0 && (
                    <span className="ml-1 text-amber-600 dark:text-amber-400">
                      -- {estimatedCount.toLocaleString()} with estimated status
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => handleStart(false)}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white shadow-lg shadow-teal-500/25 hover:shadow-teal-500/40 transition-all active:scale-[0.98]"
              >
                <Play className="h-4 w-4" />
                Start Sync
              </button>
              <button
                onClick={() => handleStart(true)}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-300 dark:hover:border-gray-500 transition-all active:scale-[0.98]"
                title="Re-check all packages including delivered and returned"
              >
                <RefreshCw className="h-4 w-4" />
                Force Sync All
              </button>
            </div>
          </div>
        </div>
      )}

      {isActive && progress && (
        <div className="relative overflow-hidden rounded-2xl border border-teal-200 dark:border-teal-800 bg-white dark:bg-gray-800">
          <div className="absolute inset-0 bg-gradient-to-r from-teal-500/5 via-cyan-500/5 to-blue-500/5" />

          <div className="relative px-5 py-4 sm:px-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center flex-shrink-0">
                  <Loader2 className="h-4 w-4 text-white animate-spin" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {state === 'retrying' ? 'Retrying Failed Codes' : 'Fetching Live Tracking Data'}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Contacting PostNL Shipping Status API...
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
                <button
                  onClick={handleCancel}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 border border-red-200 dark:border-red-800 transition-colors"
                >
                  <Square className="h-3 w-3" />
                  Cancel
                </button>
              </div>
            </div>

            <div className="mb-2">
              <div className="flex items-baseline justify-between mb-1.5">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">
                    {pct}%
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400 tabular-nums">
                    {progress.processed.toLocaleString()} / {progress.total.toLocaleString()}
                  </span>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {progress.processed > 0 &&
                    formatEta(
                      Date.now() - progress.startedAt,
                      progress.processed,
                      progress.total
                    )}
                </span>
              </div>
              <div className="w-full h-3 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-teal-500 via-cyan-500 to-blue-500 transition-all duration-500 ease-out relative"
                  style={{ width: `${Math.max(1, pct)}%` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 animate-pulse" />
                </div>
              </div>
            </div>

            {expanded && (
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700/50 flex-wrap">
                <div className="flex items-center gap-1.5 text-xs">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  <span className="text-gray-600 dark:text-gray-400">
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums">
                      {progress.succeeded.toLocaleString()}
                    </span>{' '}
                    updated
                  </span>
                </div>
                {progress.failed > 0 && (
                  <div className="flex items-center gap-1.5 text-xs">
                    <XCircle className="h-3.5 w-3.5 text-red-500" />
                    <span className="text-gray-600 dark:text-gray-400">
                      <span className="font-semibold text-red-600 dark:text-red-400 tabular-nums">
                        {progress.failed.toLocaleString()}
                      </span>{' '}
                      failed
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-1.5 text-xs">
                  <Clock className="h-3.5 w-3.5 text-gray-400" />
                  <span className="text-gray-500 dark:text-gray-400">
                    Elapsed: {formatDuration(Date.now() - progress.startedAt)}
                  </span>
                </div>
                {progress.currentCode && (
                  <div className="ml-auto text-xs font-mono text-gray-400 dark:text-gray-500 truncate max-w-[180px]">
                    {progress.currentCode}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {(state === 'completed' || state === 'cancelled') && result && (
        <div className={`relative overflow-hidden rounded-2xl border ${
          state === 'cancelled'
            ? 'border-amber-200 dark:border-amber-800'
            : result.failed === 0
              ? 'border-emerald-200 dark:border-emerald-800'
              : 'border-blue-200 dark:border-blue-800'
        } bg-white dark:bg-gray-800`}>
          <div className="relative px-5 py-4 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  state === 'cancelled'
                    ? 'bg-amber-100 dark:bg-amber-900/30'
                    : result.failed === 0
                      ? 'bg-emerald-100 dark:bg-emerald-900/30'
                      : 'bg-blue-100 dark:bg-blue-900/30'
                }`}>
                  {state === 'cancelled' ? (
                    <Square className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  ) : (
                    <CheckCircle2 className={`h-4 w-4 ${
                      result.failed === 0
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-blue-600 dark:text-blue-400'
                    }`} />
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {state === 'cancelled' ? 'Sync Cancelled' : 'Sync Complete'}
                  </h3>
                  <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                        {result.succeeded.toLocaleString()}
                      </span>{' '}
                      updated
                    </span>
                    {result.failed > 0 && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        <span className="font-semibold text-red-600 dark:text-red-400">
                          {result.failed.toLocaleString()}
                        </span>{' '}
                        failed
                      </span>
                    )}
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {formatDuration(result.duration)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {result.failed > 0 && (
                  <button
                    onClick={handleRetryFailed}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 border border-amber-200 dark:border-amber-800 transition-colors"
                  >
                    <RotateCcw className="h-3 w-3" />
                    Retry Failed ({result.failed})
                  </button>
                )}
                <button
                  onClick={handleDismiss}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
